import { useState } from "react"
import styles from "@styles/TaskCard.module.css"
import { TaskSource } from "./TaskSource"
import TaskCardHeader from "./TaskCardHeader"
import { Skeleton } from "@mui/material"
import { Icon, IconName, IconSize } from "../../icons/Icon"
import {
  CalendarEvent,
  GoogleCalendarResponseStatus,
} from "@/app/helpers/google/calendar/fetchCalendarEvents"
import { CalendarRSVPButton } from "../../common/button/fancyButtons/CalendarRSVPButton"
import clsx from "clsx"
import { updateCalendarEventRSVPStatus } from "@/app/helpers/google/calendar/updateCalendarEventRSVPStatus"
import { useCalendarContext } from "@/app/context/CalendarContext"
import { Button, ButtonVariant } from "../../common/button/Button"
import { Badge } from "../../common/badge/Badge"

export interface MeetingTaskCardContentProps {
  calendarEvent: CalendarEvent
  isMaximised?: boolean
  onMaximisedClose?: () => void
}

export default function MeetingTaskCardContent({
  calendarEvent,
  isMaximised = false,
  onMaximisedClose,
}: MeetingTaskCardContentProps) {
  const [optimisticRSVPStatus, setOptimisticRSVPStatus] =
    useState<GoogleCalendarResponseStatus | null>(null)

  const { summary, description, participants, location, meetLink } =
    calendarEvent

  const { updateCalendarEvents } = useCalendarContext()

  const title = `${summary}${description ? `: ${description}` : ""}`
  const organiser =
    participants.length > 0
      ? calendarEvent.participants?.find((p) => p.isOrganizer)?.name
      : "Calendar blocker"

  const myResponseStatus = optimisticRSVPStatus
    ? optimisticRSVPStatus
    : calendarEvent.participants?.find((p) => p.isSelf)?.responseStatus

  // Resolve event.start to "Today at XX:XX"
  const startTime = new Date(calendarEvent.start)
  const endTime = new Date(calendarEvent.end)
  const isCurrentlyRunning =
    startTime.getTime() < new Date().getTime() &&
    endTime.getTime() > new Date().getTime()
  const isAlreadyOver = endTime.getTime() < new Date().getTime()
  const isHappeningToday = startTime.getDate() === new Date().getDate()
  const isStartingInLessThanOneMinute =
    !isAlreadyOver && startTime.getTime() - new Date().getTime() < 1 * 60 * 1000
  const isHappeningInLessThanAnHour =
    !isAlreadyOver &&
    endTime.getTime() - startTime.getTime() < 1 * 60 * 60 * 1000

  const startTimeString = isCurrentlyRunning
    ? "Currently running"
    : isStartingInLessThanOneMinute
      ? `Starting in ${Math.floor((startTime.getTime() - new Date().getTime()) / 1000)}s`
      : isHappeningInLessThanAnHour
        ? `Starting in ${Math.floor((startTime.getTime() - new Date().getTime()) / 1000 / 60)}m`
        : `${
            isHappeningToday
              ? "Today"
              : `On ${startTime.toLocaleDateString("en-US", { weekday: "long" })}`
          } at ${startTime.toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          })}`

  // Duration, like 1h 30m
  const duration = isCurrentlyRunning
    ? endTime.getTime() - new Date().getTime()
    : endTime.getTime() - startTime.getTime()
  const remainingDurationHours = Math.floor(duration / 1000 / 60 / 60)
  const remainingDurationMinutes = Math.floor((duration / 1000 / 60) % 60)
  const baseDurationString = `${remainingDurationHours ? `${remainingDurationHours}h ` : ""}${remainingDurationMinutes}m`
  const durationString = isCurrentlyRunning
    ? `${baseDurationString} remaining`
    : baseDurationString

  // Check if meeting currently running or about to start (in less than 5 minutes)
  const isMeetingCurrentlyRunningOrAboutToStart =
    new Date(calendarEvent.start).getTime() <
    new Date().getTime() + 5 * 60 * 1000

  const handleRSVPClick = async (rsvpType: GoogleCalendarResponseStatus) => {
    setOptimisticRSVPStatus(rsvpType)
    const success = await updateCalendarEventRSVPStatus(calendarEvent, rsvpType)
    if (success) {
      await updateCalendarEvents()
      setOptimisticRSVPStatus(null)
    }
  }

  const handleJoinMeeting = () => {
    window.open(meetLink, "_blank")
  }

  const timeLabel = isHappeningToday
    ? `Today at ${startTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
    : `${startTime.toLocaleDateString("en-US", { weekday: "short" })} at ${startTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`

  if (isMaximised && onMaximisedClose) {
    return (
      <>
        <TaskCardHeader title="Meeting" onClose={onMaximisedClose} showContextMenuTrigger={false} />
        <div className={styles.maximisedContent}>
          <div className={styles.maximisedTaskSection}>
            <div className={styles.maximisedMetaData}>
              <Badge label="Calendar" size="Medium" color="Amber" filled={false} />
              <Badge label={timeLabel} size="Medium" color="Grey" filled={false} />
              <Badge label={durationString} size="Medium" color="Grey" filled={false} />
            </div>
            <h2 className={styles.maximisedDescription}>{summary}</h2>
            {description && (
              <p className={clsx("bodyText", styles.maximisedMeetingDescription)}>
                {description}
              </p>
            )}
            <div className={styles.maximisedMeetingDetails}>
              {organiser && (
                <div className={styles.maximisedMeetingDetailRow}>
                  <Icon
                    name={IconName.IconGroup1}
                    size={IconSize.xSmall}
                    customColor="#666"
                  />
                  <span className={clsx("subHeadline", styles.maximisedMeetingDetailText)}>
                    {organiser}
                  </span>
                </div>
              )}
              {location && (
                <div className={styles.maximisedMeetingDetailRow}>
                  <Icon
                    name={IconName.MapPin}
                    size={IconSize.xSmall}
                    customColor="#666"
                  />
                  <span className={clsx("subHeadline", styles.maximisedMeetingDetailText)}>
                    {location}
                  </span>
                </div>
              )}
              {participants.length > 0 && (
                <div className={styles.maximisedMeetingDetailRow}>
                  <Icon
                    name={IconName.IconGroup1}
                    size={IconSize.xSmall}
                    customColor="#666"
                  />
                  <span className={clsx("subHeadline", styles.maximisedMeetingDetailText)}>
                    {participants.length} participant{participants.length !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>
            {participants.length > 0 && (
              <div className={clsx(styles.footerGroup, styles.calendarRSVPContainer, styles.maximisedRSVPRow)}>
                <CalendarRSVPButton
                  rsvpType={"accepted"}
                  isSelected={myResponseStatus === "accepted"}
                  onClick={() => handleRSVPClick("accepted")}
                />
                <CalendarRSVPButton
                  rsvpType={"declined"}
                  isSelected={myResponseStatus === "declined"}
                  onClick={() => handleRSVPClick("declined")}
                />
                <CalendarRSVPButton
                  rsvpType={"tentative"}
                  isSelected={myResponseStatus === "tentative"}
                  onClick={() => handleRSVPClick("tentative")}
                />
              </div>
            )}
          </div>
        </div>
        <div className={clsx(styles.maximisedFooter, styles.maximisedFooterEmail)}>
          {meetLink && (
            <Button
              variant={ButtonVariant.Filled}
              onClick={handleJoinMeeting}
              label="Join meeting"
            />
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.header}>
        <TaskSource source={"meeting"} />
        <div className={styles.headerGroup}>
          <span className={styles.meetingStartTime}>{startTimeString}</span>
        </div>
        <div className={styles.headerGroup}>
          <Icon
            name={IconName.Clock}
            size={IconSize.xSmall}
            customColor="#666"
          />
          <span className={styles.meetingDuration}>{durationString}</span>
        </div>
        <div></div>
      </div>
      <div className={styles.descriptionContainer}>
        <span className={clsx("bodyText", styles.description)}>{title}</span>
      </div>
      <div className={clsx("subHeadline", styles.footer, styles.column)}>
        <div className={styles.row}>
          <div className={styles.footerGroup}>
            {organiser ? (
              <span>{organiser}</span>
            ) : (
              <Skeleton width={100} height={20} />
            )}
          </div>
          {location && (
            <div className={styles.footerGroup}>
              <Icon
                name={IconName.MapPin}
                size={IconSize.xSmall}
                customColor="#171717"
              />
              <span>{location}</span>
            </div>
          )}
          {participants.length > 0 && (
            <div className={styles.footerGroup}>
              <Icon
                name={IconName.IconGroup1}
                size={IconSize.xSmall}
                customColor="#171717"
              />
              <span>{"Participants"}</span>
            </div>
          )}
        </div>
        <div className={clsx(styles.row, styles.calendarRSVPRow)}>
          <div
            className={clsx(styles.footerGroup, styles.calendarRSVPContainer)}
            onClick={(e) => e.stopPropagation()}
          >
            {participants.length > 0 && (
              <>
                <CalendarRSVPButton
                  rsvpType={"accepted"}
                  isSelected={myResponseStatus === "accepted"}
                  onClick={() => handleRSVPClick("accepted")}
                />
                <CalendarRSVPButton
                  rsvpType={"declined"}
                  isSelected={myResponseStatus === "declined"}
                  onClick={() => handleRSVPClick("declined")}
                />
                <CalendarRSVPButton
                  rsvpType={"tentative"}
                  isSelected={myResponseStatus === "tentative"}
                  onClick={() => handleRSVPClick("tentative")}
                />
              </>
            )}
          </div>
          {meetLink && isMeetingCurrentlyRunningOrAboutToStart && (
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                variant={ButtonVariant.Filled}
                onClick={handleJoinMeeting}
                label="Join meeting"
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
